import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@/features';
import { closeNotifications } from '@/features/Common';
import { markNotificationAsSeen } from '@/features/Notifications';
import { Button } from '@/shared/components/ui/button';
import { type Notification } from '@/types/swagger';
import { ListItem, ListItemText, Tooltip } from '@mui/material';
import { Link } from '@tanstack/react-router';
import moment from 'moment';

interface NotificationItemProps {
  notification: Notification;
  size?: string;
}

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, size = 'small' }) => {
  const dispatch = useAppDispatch();
  const categories = useAppSelector((state) => state.notifications.notificationActions);
  const notificationAge =
    size === 'small'
      ? moment.utc(notification.created).local().fromNow()
      : moment.utc(notification.created).local().format("DD [de] MMM [']YY [a las] HH:mm:ss");

  const notificationRef = useRef<HTMLLIElement>(null);

  const category = categories.find((c) =>
    c.topics.find((t) => t.actions.some((a) => a.action_code === notification.action_code))
  );
  const topic = category?.topics.find((t) =>
    t.actions.some((a) => a.action_code === notification.action_code)
  );
  const action = topic?.actions.find((a) => a.action_code === notification.action_code);

  const getNotificationRelativeUrl = (url: string) => {
    if (!url) return '/';
    const splitUrl = url.split('/');
    const relative = splitUrl.splice(3, splitUrl.length - 1).join('/');
    if (relative.includes('?')) {
      const [paramName, id] = relative.split('?')[1].split('=');
      const fixed = relative.replace(`?${paramName}=${id}`, `:${paramName}:${id}`);
      return '/' + fixed;
    }
    return '/' + relative;
  };

  const handleMarkAsRead = async (_notification: Notification) => {
    if (notification.seen) return;
    await dispatch(markNotificationAsSeen(notification.id));
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !notification.seen) {
            observer.unobserve(entry.target);
            void dispatch(markNotificationAsSeen(notification.id));
          }
        });
      },
      {
        root: document.querySelector('.quick-notifications-list'),
        threshold: 0.8,
      }
    );
    observer.observe(notificationRef.current as HTMLLIElement);
  }, [dispatch, notification.id, notification.seen]);

  return (
    <ListItem
      ref={notificationRef}
      sx={{
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        backgroundColor: 'transparent',
        width: '100%',
        cursor: 'pointer',
        paddingY: size === 'large' ? 3 : 2,
        paddingX: size === 'large' ? 2 : 1,
      }}
      onClick={() => dispatch(closeNotifications())}
    >
      <div
        className={`d-flex flex-row justify-content-center align-items-center px-0 pl-1 ${
          notification.seen ? 'col-11' : 'col-10'
        }`}
      >
        <div className="d-flex flex-column col-12 px-0">
          <Link
            to={getNotificationRelativeUrl(JSON.parse(notification.data).url)}
            resetScroll={false}
            style={{ textDecoration: 'none', color: 'whitesmoke' }}
            onClick={async () => await handleMarkAsRead(notification)}
          >
            <span dangerouslySetInnerHTML={{ __html: notification.message }} />
            <ListItemText className="quick-notification-item-subtitle">
              {notificationAge.charAt(0).toUpperCase() + notificationAge.slice(1)}
              &nbsp;&nbsp;-&nbsp;&nbsp;
              {action ? action.action_name : ''}
            </ListItemText>
          </Link>
        </div>
      </div>
      {notification.seen ? null : (
        <div className="d-flex align-items-center justify-content-center">
          <Tooltip title="Marcar como leída" placement="left">
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => await handleMarkAsRead(notification)}
              className="$tw-h-7 $tw-rounded-full"
            >
              <div className="mark-as-read-button" />
            </Button>
          </Tooltip>
        </div>
      )}
    </ListItem>
  );
};

export default NotificationItem;
